import React, { useEffect, useState } from "react";

import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { Lens } from "@/src/components/atoms/Lens";

import { ImageLoadStatusMap } from "@/src/hooks/useProgressiveImageLoader";
import { UnifiedCarouselImage } from "@/src/types/carousel";

interface LightboxImageProps {
  image: UnifiedCarouselImage;
  index: number;
  zoomEnabled: boolean;
  isMobile: boolean;
  zoomFactor: number;
  lensSize: number;
  rotation: number;
  isImageSwitching?: boolean;
  imageLoadStatus?: ImageLoadStatusMap;
}

export const LightboxImage = ({
  image,
  index,
  zoomEnabled,
  isMobile,
  zoomFactor,
  lensSize,
  rotation,
  isImageSwitching = false,
  imageLoadStatus
}: LightboxImageProps) => {
  const theme = useTheme();
  const [useFallbackUrl, setUseFallbackUrl] = useState(false);
  const [showOriginalImage, setShowOriginalImage] = useState(false);

  const isOriginalLoaded = imageLoadStatus?.[image.id]?.original ?? false;
  const isXlLoaded = imageLoadStatus?.[image.id]?.xl ?? false;
  const hasLightboxUrl = image.lightboxUrl && image.lightboxUrl !== image.url;
  const hasOriginalUrl =
    image.originalUrl && image.originalUrl !== image.lightboxUrl;

  // Switch to original after XL has been loaded and displayed
  useEffect(() => {
    if (hasOriginalUrl && isXlLoaded && isOriginalLoaded) {
      // Small delay to ensure XL image is visible first
      const timer = setTimeout(() => {
        setShowOriginalImage(true);
      }, 100);
      return () => clearTimeout(timer);
    } else if (!isXlLoaded || !isOriginalLoaded) {
      // Reset to XL if loading status changes
      setShowOriginalImage(false);
    }
  }, [hasOriginalUrl, isXlLoaded, isOriginalLoaded]);

  const getImageSrc = (): string => {
    if (useFallbackUrl) {
      return image.url;
    }
    // Show original if XL is loaded, original is loaded, and we've set the flag to show original
    if (
      hasOriginalUrl &&
      isXlLoaded &&
      isOriginalLoaded &&
      showOriginalImage &&
      image.originalUrl
    ) {
      return image.originalUrl;
    }
    // Show XL (lightboxUrl) if available and loaded, otherwise fall back to regular URL
    if (hasLightboxUrl && isXlLoaded && image.lightboxUrl) {
      return image.lightboxUrl;
    }
    // Fallback to regular URL
    return image.url;
  };

  const imageSrc = getImageSrc();

  // For lens zoom, prefer originalUrl if available, otherwise use the same as imageSrc
  const getZoomSrc = (): string => {
    if (useFallbackUrl) {
      return imageSrc;
    }
    // Always use originalUrl for zoom if available, regardless of what's displayed
    if (image.originalUrl && isOriginalLoaded) {
      return image.originalUrl;
    }
    // Fallback to the same source as displayed image
    return imageSrc;
  };

  const zoomSrc = getZoomSrc();

  const handleImageError = () => {
    if ((hasLightboxUrl || hasOriginalUrl) && !useFallbackUrl) {
      setUseFallbackUrl(true);
      setShowOriginalImage(false);
    }
  };

  // Lens component removed for consumer pages - zoom is disabled via lightboxZoomEnabled={false}
  // Only render Lens if zoom is explicitly enabled and not on mobile
  if (zoomEnabled && !isMobile) {
    return (
      <Lens
        src={imageSrc}
        alt={image.alt ?? `Image ${index + 1}`}
        zoomFactor={zoomFactor}
        lensSize={lensSize}
        zoomSrc={zoomSrc} // Use zoomSrc instead of imageSrc
        disabled={false}
        rotation={rotation}
        onError={handleImageError}
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          zIndex: 2,
          transform: `rotate(${rotation}deg)`,
          transition: isImageSwitching ? "none" : "transform 0.3s ease"
        }}
      />
    );
  }

  return (
    <Box
      component="img"
      src={imageSrc}
      alt={image.alt ?? `Image ${index + 1}`}
      onError={handleImageError}
      draggable={false}
      sx={{
        maxWidth: "100%",
        maxHeight: "100%",
        width: "auto",
        height: "auto",
        objectFit: "contain",
        borderRadius: "8px",
        position: "relative",
        zIndex: 2,
        boxShadow: `0 8px 32px ${theme.palette.misc.backdropOverlay}`,
        transform: `rotate(${rotation}deg)`,
        transition: isImageSwitching ? "none" : "transform 0.3s ease",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
        pointerEvents: "auto",
        cursor: "default"
      }}
    />
  );
};
